/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { Fragment, useEffect, useState, useCallback } from "react";
import { Dimensions, View } from "react-native";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  TableSortLabel,
} from "@material-ui/core";
import ReactTooltip from "react-tooltip";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { RootState } from "store/types";

import { Text, LoadingView } from "UpduoLibrary";
import {
  skaleBlack,
  skaleMainBlue,
  colorWithOpacity,
  skaleWhite,
} from "config/UpduoColors";
import MemberRow from "./MemberRow";
import StoreRow from "./StoreRow";
import * as UpduoAnalytics from "config/UpduoAnalytics";

const windowHeight = Dimensions.get("window").height;

export default function SkillProgress({
  onSetMainExpandToggle,
  onExpandStore,
  mainExpandToggle,
}) {
  const dispatch = useDispatch();
  const skillProgressUserList = useSelector(
    (state: RootState) => state.skillProgressUserList
  );
  const skillProgressStoreList = useSelector(
    (state: RootState) => state.skillProgressStoreList
  );
  const skillList = useSelector((state: RootState) => state.skillList);

  const [loadingItem, setLoadingItem] = useState<null | String>(null);
  const [tableRefreshToggle, setTableRefreshToggle] = useState(false);
  const [orderBy, setOrderBy] = useState(-1);
  const [isAscending, setIsAscending] = useState(true);

  const _handleCheckIfAllExpanded = useCallback(() => {
    const populatedStoreIds = new Set();
    skillProgressUserList.forEach((user) => {
      if (user.store !== null) {
        populatedStoreIds.add(user.store);
      }
    });
    return skillProgressStoreList.length === populatedStoreIds.size;
  }, [skillProgressUserList, skillProgressStoreList]);

  useEffect(() => {
    const isAllExpanded = _handleCheckIfAllExpanded();
    if (mainExpandToggle && !isAllExpanded) {
      setLoadingItem("all");
    }
  }, [mainExpandToggle, _handleCheckIfAllExpanded]);

  const _handleCheckMainExpandToggle = useCallback(() => {
    if (skillProgressStoreList.length <= 0) {
      onSetMainExpandToggle(false);
      return;
    }
    const isAllExpanded = _handleCheckIfAllExpanded();
    onSetMainExpandToggle(isAllExpanded);
  }, [
    _handleCheckIfAllExpanded,
    skillProgressStoreList,
    onSetMainExpandToggle,
  ]);

  useEffect(() => {
    _handleCheckMainExpandToggle();
    setLoadingItem(null);
  }, [skillProgressUserList, _handleCheckMainExpandToggle]);

  const _handleGetOrder = (a, b, chosenIndex) => {
    return (
      b.user_skill_mastery_count[chosenIndex] -
      a.user_skill_mastery_count[chosenIndex]
    );
  };

  const _handleGetSkillProgress = (chosenIndex) => {
    const newSkillProgressStoreList = skillProgressStoreList;
    const sortedSkillProgressStoreList = newSkillProgressStoreList.sort(
      (a, b) => {
        const order = _handleGetOrder(a, b, chosenIndex);
        if (order !== 0) return isAscending ? order : -1 * order;
        return isAscending ? a[1] - b[1] : b[1] - a[1];
      }
    );
    dispatch(
      ActionCreators.setSkillProgressStoreList(sortedSkillProgressStoreList)
    );
    setTableRefreshToggle(!tableRefreshToggle);
    setIsAscending(!isAscending);
    setOrderBy(chosenIndex);
  };

  let columnHeaders = [
    <TableCell
      key={0}
      style={{ fontSize: 14, fontWeight: "bold", color: skaleBlack }}
    >
      <Text style={{ color: skaleBlack }}>{"Store"}</Text>
    </TableCell>,
  ];
  const expandedColumnTitleArray = ["Member", "Skale Score"];
  const expandedColumnHeaders = expandedColumnTitleArray.map(
    (item, itemIndex) => (
      <TableCell
        key={itemIndex + 1}
        style={{ fontWeight: "bold", color: skaleBlack }}
      >
        <Text
          style={{
            // When showing store details (skillProgressUserList), have column header text "Member", "Skale Score"
            // show in their original color, else when the store rows are collapsed, make the text transparent
            color: skillProgressUserList.length > 0 ? undefined : "transparent",
          }}
        >
          {item}
        </Text>
      </TableCell>
    )
  );
  columnHeaders = columnHeaders.concat(expandedColumnHeaders);
  columnHeaders.push(
    <TableCell
      key={columnHeaders.length + expandedColumnHeaders.length}
      style={{ fontWeight: "bold", color: skaleBlack }}
    >
      <Text>{"Sessions Attended"}</Text>
    </TableCell>
  );
  columnHeaders.push(
    <TableCell
      key={columnHeaders.length + expandedColumnHeaders.length + 1}
      style={{ fontWeight: "bold", color: skaleBlack }}
    >
      <Text
        data-tip
        data-for={"skillsmastered"}
        style={{
          // Show "Skills Mastered" column header text when store rows are expanded, else be transparent
          color: skillProgressUserList.length > 0 ? undefined : "transparent",
        }}
      >
        {"Lessons Mastered*"}
      </Text>
    </TableCell>
  );
  // Display all the skill names as column header with main
  const activeSkills = skillList.filter(
    (skill) => skill.is_active && skill.is_published
  );
  activeSkills.forEach((skill, skillIndex) => {
    columnHeaders.push(
      <TableCell
        key={
          columnHeaders.length + expandedColumnHeaders.length + 2 + skillIndex
        }
        style={{
          fontWeight: "bold",
          color: colorWithOpacity(skaleMainBlue, 0.7),
        }}
      >
        <TableSortLabel
          direction={isAscending ? "desc" : "asc"}
          active={orderBy === skillIndex}
          onClick={() => {
            _handleGetSkillProgress(skillIndex);
          }}
        >
          <Text>{skill.name}</Text>
        </TableSortLabel>
      </TableCell>
    );
  });
  const progressRows = skillProgressUserList.map((user, userIndex) => (
    <MemberRow
      key={userIndex}
      rowData={Object.assign(user, { is_active: true })}
      skillList={skillList}
    />
  ));

  const storeProgressRows = skillProgressStoreList.map((store, storeIndex) => (
    <StoreRow
      key={storeIndex}
      rowData={store}
      isExpanded={
        // Return correct arrow icon (downwards/right) depending on isExpanded = true/false
        // Return true if there are users in a store
        skillProgressUserList.filter((user) => user.store === store.id).length >
        0
      }
      onClick={(isExpanded) => {
        UpduoAnalytics.logEvent("user-toggle-table-expand", {
          table: "skillProgress",
          source: "row_click",
          row: storeIndex,
          row_id: store.id,
        });
        // Run _handleToggleStoreUserList to expand/collapse the store row that was clicked
        if (!isExpanded) {
          setLoadingItem(store.id);
        }
        onExpandStore(store.id);
      }}
    />
  ));

  // Use for loops to push storeProgressRows and
  //  progressRows into the table in the right order
  const allProgressRows: object[] = [];
  let loadingCounter = 0;
  skillProgressStoreList.forEach((store, storeIndex) => {
    if (storeIndex < skillProgressStoreList.length) {
      allProgressRows.push(
        // Push the store row
        <Fragment key={storeIndex}>{storeProgressRows[storeIndex]}</Fragment>
      );
    }
    skillProgressUserList.forEach((user, userIndex) => {
      if (user.store === store.id) {
        allProgressRows.push(
          // Push the store detail row
          <Fragment key={userIndex + skillProgressStoreList.length}>
            {progressRows[userIndex]}
          </Fragment>
        );
      }
    });
    if (loadingItem === "all" || loadingItem === store.id) {
      allProgressRows.push(
        <TableRow
          key={
            skillProgressStoreList.length +
            skillProgressUserList.length +
            loadingCounter
          }
        >
          <TableCell key={0} colSpan={100}>
            <LoadingView topSpacing={-5} />
          </TableCell>
        </TableRow>
      );
      loadingCounter += 1;
    }
  });

  return (
    <View style={{ zIndex: 100 }}>
      <Paper>
        <TableContainer style={{ maxHeight: windowHeight }}>
          <Table stickyHeader aria-label={"sticky table"}>
            <TableHead>
              <TableRow key={0}>{columnHeaders}</TableRow>
            </TableHead>
            <TableBody>{allProgressRows}</TableBody>
          </Table>
          <ReactTooltip id={"skillsmastered"} type={"dark"} effect={"solid"}>
            <Text style={{ color: skaleWhite, fontSize: 13 }}>
              {
                "Sum of member's total lesson progress \n (Master=1, Proficient=0.67, Novice=0.33)"
              }
            </Text>
          </ReactTooltip>
        </TableContainer>
      </Paper>
    </View>
  );
}
